import { useEffect, useMemo, useState } from 'react';
import { queryOrdersByDay, deleteOrder, updateOrderStatus } from '@paulownia/shared/services/orders';
import { getCachedProducts } from '@paulownia/shared/services/products-cache';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { type Order, type Product, getOptionLabels } from '@paulownia/shared/types';
import { Input } from '@paulownia/shared/components/ui/input';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { getTodayKey } from '@paulownia/shared/lib/date-utils';
import { useProductLookup } from '@paulownia/shared/hooks/useProductLookup';
import { useTenant } from '@paulownia/shared/lib/tenant-context';

export function HistoryView() {
  const { currentTenantId } = useTenant();
  const [date, setDate] = useState(() => getTodayKey());
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Use the optimized product lookup hook
  const { getName: getProductName } = useProductLookup(products);

  useEffect(() => {
    if (!currentTenantId) {
      setOrders([]);
      return;
    }
    queryOrdersByDay(date, currentTenantId).then((o) => {
      setOrders(o.filter((ord) => ord.status === 'done').sort((a, b) => (a.pickupAtISO < b.pickupAtISO ? 1 : -1)));
    });
  }, [date, currentTenantId]);

  useEffect(() => {
    if (!currentTenantId) {
      setProducts([]);
      return;
    }

    // Use cached products instead of real-time listener
    getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
      reportError(error, { context: 'HistoryView.loadProducts', tenantId: currentTenantId });
    });

    // Refresh every 5 minutes
    const interval = setInterval(() => {
      getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
        reportError(error, { context: 'HistoryView.refreshProducts', tenantId: currentTenantId });
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentTenantId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return orders;
    const s = search.trim().toLowerCase();
    return orders.filter((o) => o.customerName.toLowerCase().includes(s));
  }, [orders, search]);

  const handleDeleteOrder = async (orderId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return;
    }

    if (!currentTenantId) {
      toast.error('Tenant non défini');
      return;
    }

    try {
      await deleteOrder(orderId);
      toast.success('Commande supprimée');
      setSelectedOrder(null);
      // Refresh orders
      const o = await queryOrdersByDay(date, currentTenantId);
      setOrders(o.filter((ord) => ord.status === 'done').sort((a, b) => (a.pickupAtISO < b.pickupAtISO ? 1 : -1)));
    } catch (e) {
      const error = e as Error;
      toast.error(error?.message || 'Erreur lors de la suppression');
      console.error('Error deleting order:', e);
    }
  };

  const handleRestoreOrder = async (orderId: string, newStatus: 'pending' | 'ready') => {
    if (!currentTenantId) {
      toast.error('Tenant non défini');
      return;
    }

    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success(`Commande remise en "${newStatus === 'pending' ? 'à préparer' : 'prête'}"`);
      setSelectedOrder(null);
      // Refresh orders
      const o = await queryOrdersByDay(date, currentTenantId);
      setOrders(o.filter((ord) => ord.status === 'done').sort((a, b) => (a.pickupAtISO < b.pickupAtISO ? 1 : -1)));
    } catch (e) {
      const error = e as Error;
      toast.error(error?.message || 'Erreur lors de la modification');
      console.error('Error updating order status:', e);
    }
  };

  return (
    <>
      <div className="pb-16 p-4 space-y-3">
        <h1 className="text-xl font-semibold">Historique</h1>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="text-base" style={{ fontSize: '16px' }} />
          <Input placeholder="Recherche par nom" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="space-y-2">
          {filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedOrder(o)}
              className="w-full rounded-md border bg-white p-3 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{o.customerName}</div>
                <div className="text-sm text-gray-500">
                  {new Date(o.pickupAtISO).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                {o.lines.length} produit{o.lines.length > 1 ? 's' : ''}
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="text-sm text-gray-500">Aucune commande</div>}
        </div>
      </div>

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la commande</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Client</span>
                  <span className="font-medium">{selectedOrder.customerName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Heure de récupération</span>
                  <span className="font-medium">
                    {new Date(selectedOrder.pickupAtISO).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Date</span>
                  <span className="font-medium">
                    {new Date(selectedOrder.pickupAtISO).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Statut</span>
                  <Badge variant="secondary">Récupérée</Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Produits</h3>
                <div className="space-y-2">
                  {selectedOrder.lines.map((line, idx) => (
                    <div key={idx} className="flex items-start justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{line.quantity}×</span>
                          <span className="font-medium">{getProductName(line.productId)}</span>
                        </div>
                        {(() => {
                          const product = products.find(p => p.id === line.productId);
                          if (!product) return null;
                          const optionLabels = getOptionLabels(line, product);
                          return optionLabels.length > 0 && (
                            <div className="flex gap-2 mt-1">
                              {optionLabels.map((label, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{label}</Badge>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 flex flex-col gap-2">
                <div className="font-medium mb-2">Actions</div>
                <Button
                  variant="outline"
                  onClick={() => handleRestoreOrder(selectedOrder.id, 'pending')}
                  className="w-full justify-start"
                >
                  Remettre en "À préparer"
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleRestoreOrder(selectedOrder.id, 'ready')}
                  className="w-full justify-start"
                >
                  Remettre en "Prête"
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteOrder(selectedOrder.id)}
                  className="w-full justify-start"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer la commande
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


